import React, { useMemo, useState } from "react";
import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Box,
} from "@chakra-ui/react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import Card from "components/card/Card";
import { FiEdit, FiEye, FiTrash2 } from "react-icons/fi";
import DeleteModal from "components/DeleteModal/DeleteModal";

const TermsTable = ({ tableData, onViewDetails, onDeleteTerm, onEdit }) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [termToDelete, setTermToDelete] = useState(null);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const columnsData = useMemo(
    () => [
      { Header: "Terms Title", accessor: "title" },
      { Header: "Clients", accessor: "invoiceCount" },
      { Header: "Advance Rate", accessor: "advanceRate" },
      { Header: "Purchase Rate", accessor: "purchaseRate" },
      { Header: "Escrow Rate", accessor: "escrowReserveRate" },
      { Header: "ACTION", accessor: "action" },
    ],
    []
  );

  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: 1,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = tableInstance;

  const handleDeleteClick = (e, term) => {
    e.stopPropagation();
    setTermToDelete(term);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDeleteTerm(termToDelete._id);
    setIsDeleteModalOpen(false);
    setTermToDelete(null);
  };

  const handleEditClick = (e, term) => {
    e.stopPropagation();
    onEdit(term);
  };

  return (
    <>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ sm: "scroll", lg: "hidden" }}
      >
        <Box>
          <Table {...getTableProps()} variant="simple" color="gray.500">
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      pe="10px"
                      key={index}
                      borderColor={borderColor}
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.400"
                      >
                        {column.render("Header")}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr 
                    {...row.getRowProps()} 
                    key={index}
                    onClick={() => onViewDetails(row.original)}
                    cursor="pointer"
                    _hover={{
                      bg: "gray.50"
                    }}
                  >
                    {row.cells.map((cell, index) => {
                      let data;
                      if (cell.column.Header === "ACTION") {
                        data = (
                          <Box display="flex" gap={2}>
                            <IconButton
                              icon={<FiEye />}
                              variant="ghost"
                              size="sm"
                              colorScheme="blue"
                              aria-label="View"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewDetails(row.original);
                              }}
                            />
                            <IconButton
                              icon={<FiEdit />}
                              variant="ghost"
                              size="sm"
                              colorScheme="green"
                              aria-label="Edit"
                              onClick={(e) => handleEditClick(e, row.original)}
                            />
                            <IconButton
                              icon={<FiTrash2 />}
                              variant="ghost"
                              size="sm"
                              colorScheme="red"
                              aria-label="Delete"
                              onClick={(e) => handleDeleteClick(e, row.original)}
                              isDisabled={row.original.invoiceCount !== 0}
                            />
                          </Box>
                        );
                      } else if (cell.column.Header === "Terms Title") {
                        data = (
                          <Text
                            color="#602CE8"
                            fontSize="sm"
                            fontWeight="500"
                            cursor="pointer"
                            _hover={{
                              textDecoration: "underline"
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewDetails(row.original);
                            }}
                          >
                            {cell.value}
                          </Text>
                        );
                      } else if (
                        cell.column.Header === "Advance Rate" || 
                        cell.column.Header === "Purchase Rate" || 
                        cell.column.Header === "Escrow Rate"
                      ) {
                        data = (
                          <Text color={textColor} fontSize="sm" fontWeight="400">
                            {cell.value}%
                          </Text>
                        );
                      } else {
                        data = (
                          <Text color={textColor} fontSize="sm" fontWeight="400">
                            {cell.value}
                          </Text>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          fontSize={{ sm: "14px" }}
                          minW={{ sm: "150px", md: "200px", lg: "auto" }}
                          borderColor={borderColor}
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Card>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        icon={FiTrash2}
        heading="Delete Term"
        description="Are you sure you want to delete this term? This action cannot be undone."
        deleteButtonText="Delete Term"
        cancelButtonText="Cancel"
        type="delete"
      />
    </>
  );
};

export default TermsTable;
